import { evalQuotedToken, Tokenizer, TypeGuards } from 'liquidjs';
import type { PropertyAccessToken } from 'liquidjs';

type FilterToken = ReturnType<Tokenizer['readFilteredValue']>['filters'][number];

export type NumberKind = 'money' | 'units' | 'perUnit';

const DEFAULT_NUMBER_FILTERS: Readonly<Record<NumberKind, string>> = {
	money: 'number: 2, 2',
	// Stored units are whole multiples of 1/unitDenominator, and the largest denominator is 10^5.
	units: 'number: 0, 5',
	// A nominal value of 0.000012 must survive.
	perUnit: 'number: 2, 10',
};

/** Every number under the `data` root, keyed by its path with list indexes collapsed to `[]`. */
export const NUMBER_KINDS: Readonly<Record<string, NumberKind>> = {
	'holdings[].unitsHeld': 'units',
	'holdings[].fairMarketValue': 'money',
	'holdings[].costOfAcquisition': 'money',
	'holdings[].votes': 'units',
	'holdings[].security.nominalValue': 'perUnit',
	'holdings[].security.votesPerUnit': 'units',
	'holdings[].lastTransaction.units': 'units',
	'holdings[].lastTransaction.cost': 'money',
	'holdings[].transactions[].units': 'units',
	'holdings[].transactions[].cost': 'money',
	'allocations[].security.nominalValue': 'perUnit',
	'allocations[].security.votesPerUnit': 'units',
	'allocations[].units': 'units',
	'allocations[].cost': 'money',
	'loans[].totalValue': 'money',
};

interface ValuePath {
	path: string;
	// A list of values: no number filter can format one.
	list: boolean;
}

type LoopScope = { variable: string; elementPath: string | undefined }[];

const elementPath = (value: ValuePath): string => (value.list ? value.path : `${value.path}[]`);

const child = (path: string, name: string): string => (path === '' ? name : `${path}.${name}`);

const quotedArg = (filter: FilterToken): string | undefined => {
	const [arg] = filter.args;
	return TypeGuards.isQuotedToken(arg) ? evalQuotedToken(arg) : undefined;
};

// The innermost loop variable shadows any outer one of the same name.
const rootPath = (root: string, loops: LoopScope): string | undefined => {
	const loop = [...loops].reverse().find((scope) => scope.variable === root);
	if (loop) {
		return loop.elementPath;
	}
	return root === 'data' ? '' : undefined;
};

const resolvePropertyPath = (
	token: PropertyAccessToken,
	loops: LoopScope
): ValuePath | undefined => {
	const [root, ...rest] = token.props;
	if (token.variable !== undefined || !TypeGuards.isWordToken(root)) {
		return undefined;
	}
	let path = rootPath(root.content, loops);
	for (const prop of rest) {
		if (path === undefined) {
			return undefined;
		}
		if (TypeGuards.isWordToken(prop)) {
			path = child(path, prop.content);
		} else if (TypeGuards.isQuotedToken(prop)) {
			path = child(path, evalQuotedToken(prop));
		} else if (TypeGuards.isNumberToken(prop) || TypeGuards.isPropertyAccessToken(prop)) {
			path = `${path}[]`;
		} else {
			return undefined;
		}
	}
	return path === undefined ? undefined : { path, list: false };
};

// Only the filters that select a value; any other filter means the author chose the output.
const applyStructuralFilter = (value: ValuePath, filter: FilterToken): ValuePath | undefined => {
	switch (filter.name) {
		case 'where':
			return { path: elementPath(value), list: true };
		case 'map': {
			const property = quotedArg(filter);
			return property === undefined
				? undefined
				: { path: child(elementPath(value), property), list: true };
		}
		case 'first':
		case 'last':
			return { path: elementPath(value), list: false };
		case 'sum': {
			if (filter.args.length === 0) {
				return { path: elementPath(value), list: false };
			}
			const property = quotedArg(filter);
			return property === undefined
				? undefined
				: { path: child(elementPath(value), property), list: false };
		}
		default:
			return undefined;
	}
};

const numberKindOf = (expression: string, loops: LoopScope): NumberKind | undefined => {
	const { initial, filters } = new Tokenizer(expression).readFilteredValue();
	const [head, ...others] = initial.postfix;
	if (others.length > 0 || !TypeGuards.isPropertyAccessToken(head)) {
		return undefined;
	}
	let value = resolvePropertyPath(head, loops);
	for (const filter of filters) {
		value = value && applyStructuralFilter(value, filter);
	}
	return value && !value.list ? NUMBER_KINDS[value.path] : undefined;
};

const loopScopeOf = (tagArgs: string, loops: LoopScope): LoopScope[number] => {
	const tokenizer = new Tokenizer(tagArgs);
	const variable = tokenizer.readIdentifier().content;
	tokenizer.skipBlank();
	const keyword = tokenizer.readIdentifier().content;
	const collection = tokenizer.readValue();
	const resolved =
		keyword === 'in' && TypeGuards.isPropertyAccessToken(collection)
			? resolvePropertyPath(collection, loops)
			: undefined;
	return { variable, elementPath: resolved && elementPath(resolved) };
};

/**
 * Gives every bound number the author left unformatted its platform default format.
 *
 * Applied at render time, so templates saved before a default existed or changed pick it up. An
 * output carrying any formatting filter is the author's choice and is left alone.
 */
export const withDefaultNumberFormats = (template: string): string => {
	const insertions: { at: number; filter: string }[] = [];
	const loops: LoopScope = [];
	try {
		for (const token of new Tokenizer(template).readTopLevelTokens()) {
			if (TypeGuards.isTagToken(token)) {
				if (token.name === 'for') {
					loops.push(loopScopeOf(token.args, loops));
				} else if (token.name === 'endfor') {
					loops.pop();
				}
			} else if (TypeGuards.isOutputToken(token)) {
				const kind = numberKindOf(token.content, loops);
				if (kind !== undefined) {
					insertions.push({
						at: token.contentRange[1],
						filter: DEFAULT_NUMBER_FILTERS[kind],
					});
				}
			}
		}
	} catch {
		// Malformed Liquid fails the same way at render whether or not it was rewritten.
		return template;
	}

	let result = '';
	let from = 0;
	for (const { at, filter } of insertions) {
		result += `${template.slice(from, at)} | ${filter}`;
		from = at;
	}
	return result + template.slice(from);
};
